// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import { useMergedModalVisibility } from "../../common/useMergedModalVisibility";
import { getNestedModalProps } from "../../common/nestedModalProps";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AddCountryModal from "./add_country";
import AddProvinceModal from "./add_province";

// Variables
window.Swal = Swal;

// API endpoints - Using regions API
const ADD_CITY = "https://ultima.icsgr.com/api/regions/all_cities/";
const GET_COUNTRIES = "https://ultima.icsgr.com/api/regions/all_countries/";
const GET_PROVINCES = "https://ultima.icsgr.com/api/regions/all_provinces/";

// Helpers
const clampLen = (value, max) => value.slice(0, max);
const toSmallInt = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return "";
  return Math.max(-32768, Math.min(32767, n)); // Django SmallIntegerField range
};

function AddCityModal({
  defaultCountryId,
  defaultProvinceId,
  lockCountry = false,
  lockProvince = false,
  refreshData,
  onCityCreated,
  show: showProp,
  onHide: onHideProp,
  hideTrigger = false,
  nested = false,
  stackLevel = 1,
}) {
  const { show, open, close } = useMergedModalVisibility(showProp, onHideProp);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [addCountryOpen, setAddCountryOpen] = useState(false);
  const [addProvinceOpen, setAddProvinceOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [countryId, setCountryId] = useState(""); // maps to country_id
  const [provinceId, setProvinceId] = useState(""); // maps to province_id
  const [orderindex, setOrderindex] = useState(""); // required

  const resetForm = () => {
    setTitle("");
    setCountryId("");
    setProvinceId("");
    setOrderindex("");
  };

  const handleClose = () => {
    close();
    resetForm();
  };
  const handleShow = () => {
    resetForm();
    if (defaultCountryId) setCountryId(defaultCountryId);
    if (defaultProvinceId) setProvinceId(defaultProvinceId);
    open();
  };

  const reloadCountries = () => {
    const currentHeaders = {
      ...headers,
      Authorization: "Token " + localStorage.getItem("userToken"),
    };
    return axios
      .get(GET_COUNTRIES, { headers: currentHeaders })
      .then((res) => {
        const list = res.data?.all_countries || [];
        setCountries(list);
        return list;
      })
      .catch(() => {
        setCountries([]);
        return [];
      });
  };

  const isTitleValid = title.trim().length >= 2 && title.trim().length <= 40;
  const isCountryIdValid = countryId.length > 0;
  const isProvinceIdValid = provinceId.length > 0;
  const isOrderIndexValid = orderindex !== "" && Number.isInteger(+orderindex);

  const isFormValid = isTitleValid && isCountryIdValid && isProvinceIdValid && isOrderIndexValid;
  // Fetch countries for dropdown
  useEffect(() => {
    if (show) {
      // Try using axios directly with proper headers
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      
      console.log('Fetching countries from:', GET_COUNTRIES);
      console.log('Using headers:', currentHeaders);
      
      reloadCountries();
    }
  }, [show]);

  // When used as a nested/controlled modal (opened by parent), handleShow() is not called.
  // Ensure the locked/default country/province are pre-selected when the modal becomes visible.
  useEffect(() => {
    if (!show) return;

    if (defaultCountryId && (lockCountry || !countryId)) {
      setCountryId(defaultCountryId);
    }
    if (defaultProvinceId && (lockProvince || !provinceId)) {
      setProvinceId(defaultProvinceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultCountryId, defaultProvinceId, lockCountry, lockProvince]);

  // Reset province when country changes (unless locked)
  useEffect(() => {
    if (!lockProvince) {
      setProvinceId("");
    }
  }, [countryId, lockProvince]);

  // Ensure default province is applied when modal opens with locks
  useEffect(() => {
    if (show && lockProvince && defaultProvinceId) {
      setProvinceId(defaultProvinceId);
    }
  }, [show, lockProvince, defaultProvinceId]);

  // Fetch provinces when country changes
  useEffect(() => {
    if (countryId && show) {
      // Try using axios directly with proper headers
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      
      console.log('Fetching provinces from:', GET_PROVINCES);
      console.log('Fetching provinces for country:', countryId);
      
      axios.get(GET_PROVINCES, { headers: currentHeaders })
        .then((res) => {
          console.log('Provinces API response:', res.data);
          // Handle different response structures
                     const allProvinces = res.data?.all_provinces || [];
          console.log('All provinces data:', allProvinces);
          // Filter provinces by country
          const provincesData = allProvinces.filter(province => province.country?.country_id === countryId);
          console.log('Filtered provinces data:', provincesData);
          console.log('Setting provinces state with:', provincesData);
          setProvinces(provincesData);
        })
        .catch((e) => {
          console.error("Failed to fetch provinces:", e);
          console.error("Error response:", e.response?.data);
          setProvinces([]);
        });
    } else {
      setProvinces([]);
    }
  }, [countryId, show]);

  const createNewCity = async () => {
    try {
      // Update headers with current token
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const res = await axios({
        method: "post",
        url: ADD_CITY,
        headers: currentHeaders,
        data: {
          title: title.trim(),
          country_id: countryId,
          province_id: provinceId,
          orderindex: Number(orderindex),
        },
      });
      const created = res?.data;

      // Always close immediately on success, even if callbacks fail.
      handleClose();

      try {
        if (typeof refreshData === "function") refreshData();
      } catch (_) {}

      if (typeof onCityCreated === "function") {
        try {
          await Promise.resolve(onCityCreated(created));
        } catch (_) {}
      }
    } catch (e) {
      console.log('Error creating city:', e);
      console.log('Error response data:', e?.response?.data);
      
      // Handle different error response formats
      let apiMsg = "Something went wrong while creating the city.";
      
      if (e?.response?.data?.error) {
        // Custom error format from our enhanced error handling
        apiMsg = e.response.data.error;
      } else if (e?.response?.data?.country_id) {
        apiMsg = Array.isArray(e.response.data.country_id) ? e.response.data.country_id[0] : e.response.data.country_id;
      } else if (e?.response?.data?.country) {
        apiMsg = Array.isArray(e.response.data.country) ? e.response.data.country[0] : e.response.data.country;
      } else if (e?.response?.data?.province_id) {
        apiMsg = Array.isArray(e.response.data.province_id) ? e.response.data.province_id[0] : e.response.data.province_id;
      } else if (e?.response?.data?.title) {
        apiMsg = Array.isArray(e.response.data.title) ? e.response.data.title[0] : e.response.data.title;
      } else if (e?.response?.data?.orderindex) {
        // Serializer validation error for orderindex
        apiMsg = e.response.data.orderindex[0];
      } else if (e?.response?.data?.detail) {
        apiMsg = e.response.data.detail;
      }
      
      Swal.fire({
        title: "Error",
        text: apiMsg,
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
          <BiPlus
            style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
          />
          Create new City
        </Button>
      )}

      <GuardedModal
        show={show}
        size="lg"
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        {...(nested ? getNestedModalProps(stackLevel) : {})}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new City</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>City:</Form.Label>
                  <Form.Control
                    maxLength={40}
                    placeholder="e.g., ATHENS"
                    onChange={(e) =>
                      setTitle(clampLen(e.target.value.toUpperCase(), 40))
                    }
                    value={title}
                  />
                </Form.Group>

                <SelectWithInlineCreate
                  className="mb-3"
                  label="Country"
                  required
                  value={countryId}
                  onChange={setCountryId}
                  disabled={lockCountry}
                  placeholderOption="Select Country"
                  onAddClick={lockCountry ? undefined : () => setAddCountryOpen(true)}
                  overviewLinkEntity="country"
                >
                  {Array.isArray(countries) && countries.map((country) => (
                    <option key={country.country_id} value={country.country_id} title={country.country_id}>
                      {country.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>

                <SelectWithInlineCreate
                  className="mb-3"
                  label="Province"
                  required
                  value={provinceId}
                  onChange={setProvinceId}
                  disabled={!countryId || lockProvince}
                  placeholderOption="Select Province"
                  onAddClick={
                    !countryId || lockProvince
                      ? undefined
                      : () => setAddProvinceOpen(true)
                  }
                  overviewLinkEntity="province"
                >
                  {Array.isArray(provinces) && provinces.map((province) => (
                    <option key={province.province_id} value={province.province_id} title={province.province_id}>
                      {province.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>

                <Form.Group className="mb-3">
                  <Form.Label>Order by*:</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 1"
                    onChange={(e) => setOrderindex(toSmallInt(e.target.value))}
                    value={orderindex}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isTitleValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title is required (2–40 chars).
                  </li>
                )}
                {!isCountryIdValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Country is required.
                  </li>
                )}
                {!isProvinceIdValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Province is required.
                  </li>
                )}
                {!isOrderIndexValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Order index is required.
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={createNewCity}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          const id = data?.country_id;
          await reloadCountries();
          if (id) setCountryId(id);
          setAddProvinceOpen(false);
          setAddCountryOpen(false);
        }}
      />
      <AddProvinceModal
        nested
        hideTrigger
        show={addProvinceOpen}
        onHide={() => setAddProvinceOpen(false)}
        defaultCountryId={countryId || defaultCountryId}
        lockCountry
        onProvinceCreated={async (data) => {
          const id = data?.province_id;
          setAddProvinceOpen(false);
          if (id && countryId) {
            const currentHeaders = {
              ...headers,
              Authorization: "Token " + localStorage.getItem("userToken"),
            };
            try {
              const res = await axios.get(GET_PROVINCES, { headers: currentHeaders });
              const allProvinces = res.data?.all_provinces || [];
              setProvinces(allProvinces.filter((p) => p.country?.country_id === countryId));
            } catch {
              setProvinces([]);
            }
            setProvinceId(id);
          }
        }}
      />
    </>
  );
}

export default AddCityModal; 